// libs
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { Location } from 'history';

// components
import Portal from 'shared/components/Portal';
import OverlaySheetContext from 'shared/components/OverlaySheet/OverlaySheetContext';

// hooks
import useFocusTrap from 'shared/hooks/useFocusTrap';
import useVerticalNavigationGesture from 'shared/hooks/useVerticalNavigationGesture';

// helpers
import { log } from 'shared/helpers/newRelic';
import { ProductJourneyContext, ProductJourneyLifecycle } from 'shared/components/SmartProductJourney/context';
import { getJourneyDestination, getStagePosition, hasVisibleDialog } from 'shared/components/SmartProductJourney/helpers';

// constants
import { HISTORY_KEY, RESTORE_KEY, DURATION } from 'shared/components/SmartProductJourney/constants';

// styles
import { CarouselStage, RouteStage } from 'shared/components/SmartProductJourney/styles';

type Phase = 'carousel' | 'forward' | 'pdp' | 'backward';

interface Session {
  lifecycle?: ProductJourneyLifecycle;
  content: ReactNode;
  title: string;
  source: Location;
  token: string;
  destination?: string;
  scrollY: number;
  trigger: HTMLElement | null;
}

/** Keeps the carousel mounted while the normal router owns the one live PDP. */
export default function SmartProductJourney({ children }: { children: ReactNode }) {
  const history = useHistory();
  const [session, setSession] = useState<Session | null>(null);
  const [phase, setPhase] = useState<Phase>('carousel');
  const sessionRef = useRef(session);
  const phaseRef = useRef(phase);
  const routeRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const frame = useRef<number>();
  const offset = useRef(0);
  const settling = useRef(false);
  const height = useRef(0);
  const focusRef = useFocusTrap(Boolean(session) && phase === 'carousel', { autoFocusDelay: 0 });

  const changePhase = useCallback((next: Phase) => {
    phaseRef.current = next;
    setPhase(next);
  }, []);

  const clearAnimation = useCallback(() => {
    clearTimeout(timer.current);
    if (frame.current !== undefined) cancelAnimationFrame(frame.current);
    settling.current = false;
  }, []);

  const paint = useCallback((next: number, animated = false) => {
    const route = routeRef.current;
    const carousel = carouselRef.current;
    if (!route || !carousel) return;
    const position = getStagePosition(next, height.current, animated);
    offset.current = position.offset;
    route.style.transition = position.transition;
    carousel.style.transition = position.transition;
    route.style.transform = position.routeTransform;
    carousel.style.transform = position.carouselTransform;
  }, []);

  const settle = useCallback((next: number, done: VoidFunction) => {
    clearAnimation();
    settling.current = true;
    const reduced = typeof window === 'undefined' ||
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (reduced) {
      paint(next);
      settling.current = false;
      done();
      return;
    }
    // Start the transition only after both stages have their gesture positions painted.
    frame.current = requestAnimationFrame(() => {
      paint(next, true);
      timer.current = setTimeout(() => {
        settling.current = false;
        done();
      }, DURATION);
    });
  }, [clearAnimation, paint]);

  const open = useCallback((content: ReactNode, title: string, lifecycle?: ProductJourneyLifecycle) => {
    if (sessionRef.current) return;
    const restoring = history.location.state?.[HISTORY_KEY] && !history.location.state?.productJourneyPDP;
    const token = restoring ? history.location.state[HISTORY_KEY] : `${Date.now()}-${history.location.key || ''}`;
    const next = { content, title, token, lifecycle, source: history.location,
      scrollY: window.scrollY, trigger: document.activeElement as HTMLElement | null };
    sessionRef.current = next;
    setSession(next);
    changePhase('carousel');
    if (!restoring) history.push({ ...history.location, state: { ...history.location.state, [HISTORY_KEY]: token } });
  }, [changePhase, history]);

  const remember = useCallback((data: unknown) => {
    if (!sessionRef.current || history.location.state?.productJourneyPDP) return;
    try {
      history.replace({ ...history.location, state: { ...history.location.state, [RESTORE_KEY]: data } });
    } catch {
      // Storage quotas must not interrupt the active carousel journey.
      log({ errTitle: 'Unable to retain carousel history', tags: { module: 'SmartProductJourney' } });
    }
  }, [history]);

  const prepare = useCallback((url: string) => {
    const current = sessionRef.current;
    if (!current || phaseRef.current !== 'carousel' || settling.current) return false;
    const destination = getJourneyDestination(url, window.location.href, history.createHref({ pathname: '/' }));
    if (!destination) return false;
    current.destination = destination;
    height.current = window.innerHeight;
    changePhase('forward');
    // Imperative styles avoid an initial frame with the entering page at y=0.
    routeRef.current?.setAttribute('data-moving', 'true');
    paint(0);
    history.push(current.destination, { [HISTORY_KEY]: current.token, productJourneyPDP: true });
    return true;
  }, [changePhase, history, paint]);

  const navigate = useCallback((url: string, beforeNavigate?: VoidFunction) => {
    if (settling.current) return;
    prepare(url);
    if (phaseRef.current !== 'forward') return;
    beforeNavigate?.();
    settle(height.current, () => {
      changePhase('pdp');
      if (routeRef.current) {
        routeRef.current.style.transform = '';
        routeRef.current.style.transition = '';
      }
      window.scrollTo(0, 0);
    });
  }, [changePhase, prepare, settle]);

  const progress = useCallback((distance: number) => {
    if (settling.current) return;
    if (phaseRef.current === 'forward') paint(distance);
    if (phaseRef.current === 'backward') paint(height.current - distance);
  }, [paint]);

  const cancel = useCallback(() => {
    if (settling.current) return;
    if (phaseRef.current === 'forward') {
      settle(0, () => history.goBack());
    } else if (phaseRef.current === 'backward') {
      settle(height.current, () => {
        changePhase('pdp');
        if (routeRef.current) routeRef.current.style.transform = '';
      });
    }
  }, [changePhase, history, settle]);

  const beginReturn = useCallback(() => {
    if (phaseRef.current !== 'pdp' || settling.current) return false;
    height.current = window.innerHeight;
    changePhase('backward');
    routeRef.current?.setAttribute('data-moving', 'true');
    paint(height.current);
    return true;
  }, [changePhase, paint]);

  const returnToCarousel = useCallback(() => {
    if (settling.current) return;
    beginReturn();
    settle(0, () => history.goBack());
  }, [beginReturn, history, settle]);

  const canReturn = useCallback(() => Boolean(sessionRef.current) && !settling.current &&
    (phaseRef.current === 'pdp' || phaseRef.current === 'backward') && window.scrollY <= 1 &&
    !hasVisibleDialog(), []);
  const feedback = useMemo(() => ({ onStart: beginReturn, onProgress: progress, onCancel: cancel }),
    [beginReturn, progress, cancel]);
  useVerticalNavigationGesture(routeRef, 'down',
    session && (phase === 'pdp' || phase === 'backward') ? returnToCarousel : undefined,
    canReturn, feedback);

  const close = useCallback(() => {
    if (phaseRef.current === 'carousel' && !settling.current) history.goBack();
  }, [history]);

  useEffect(() => history.listen((location) => {
    const current = sessionRef.current;
    if (!current) return;
    if (location.state?.[HISTORY_KEY] === current.token) {
      if (location.state?.productJourneyPDP) {
        if (phaseRef.current !== 'forward') {
          clearAnimation();
          changePhase('pdp');
        }
      } else {
        clearAnimation();
        changePhase('carousel');
        paint(0);
      }
      return;
    }
    clearAnimation();
    sessionRef.current = null;
    setSession(null);
    changePhase('carousel');
    if (routeRef.current) {
      routeRef.current.style.transform = '';
      routeRef.current.style.transition = '';
    }
    if (location.pathname === current.source.pathname) {
      current.lifecycle?.onClose?.();
      frame.current = requestAnimationFrame(() => {
        window.scrollTo(0, current.scrollY);
        if (current.trigger?.isConnected) current.trigger.focus({ preventScroll: true });
      });
    }
  }), [changePhase, clearAnimation, history, paint]);

  useEffect(() => {
    if (!session || phase === 'pdp') return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [session, phase]);

  useEffect(() => {
    if (!session) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (hasVisibleDialog()) return;
      event.preventDefault();
      if (phaseRef.current === 'carousel') close();
      else if (phaseRef.current === 'pdp') returnToCarousel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [session, close, returnToCarousel]);

  useEffect(() => clearAnimation, [clearAnimation]);

  useEffect(() => {
    const route = routeRef.current;
    if (!session || phase !== 'pdp' || !route) return;

    const focusContent = () => {
      const content = route.querySelector<HTMLElement>('#main-content');
      if (!content) return false;
      content.focus({ preventScroll: true });
      return true;
    };

    if (focusContent()) return;
    const observer = new MutationObserver(() => {
      if (focusContent()) observer.disconnect();
    });
    observer.observe(route, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [session, phase]);

  const moving = Boolean(session) && (phase === 'forward' || phase === 'backward');
  const onPDPView = useCallback(() => sessionRef.current?.lifecycle?.onPDPView?.(), []);
  const carouselActive = Boolean(session) && phase === 'carousel';
  const canSwipe = Boolean(session) && (phase === 'carousel' || phase === 'forward');
  const value = useMemo(() => ({ open, close, prepare, navigate, progress, cancel, remember,
    carouselActive: canSwipe, isPDPVisible: !session || phase === 'pdp', onPDPView }),
    [open, close, prepare, navigate, progress, cancel, remember, canSwipe, session, phase, onPDPView]);

  return (
    <ProductJourneyContext.Provider value={value}>
      <RouteStage ref={routeRef} data-moving={moving} aria-hidden={session && phase !== 'pdp' ? true : undefined}>
        {children}
      </RouteStage>
      {session && (
        <Portal>
          <CarouselStage ref={carouselRef} data-moving={moving} data-inactive={phase === 'pdp'}
            data-product-journey role={carouselActive ? 'dialog' : undefined}
            aria-modal={carouselActive ? true : undefined} aria-label={session.title}
            aria-hidden={!carouselActive}>
            <div ref={focusRef} style={{ height: '100%' }}>
              <OverlaySheetContext.Provider value={{ doHide: close }}>
                {session.content}
              </OverlaySheetContext.Provider>
            </div>
          </CarouselStage>
        </Portal>
      )}
    </ProductJourneyContext.Provider>
  );
}
